import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { FormManagementTabId } from '@/contracts/form-management'
import { formManagementTabLabels } from '@/contracts/form-management'
import { cn } from '@/lib/utils'
import {
  BarChart3,
  Bell,
  CheckCircle2,
  Download,
  GitCompare,
  Link2,
  MousePointerClick,
  Settings,
  Users,
} from 'lucide-react'
import type { KeyboardEvent } from 'react'

const tabIcons = {
  analytics: BarChart3,
  export: Download,
  crossAnalysis: GitCompare,
  visitorActivity: MousePointerClick,
  linkGenerator: Link2,
  notifications: Bell,
  completionPage: CheckCircle2,
  customerWorkflow: Users,
  operations: Settings,
} satisfies Record<FormManagementTabId, typeof CheckCircle2>

type FormManagementNavProps = {
  activeTab: FormManagementTabId | null
  tabs: FormManagementTabId[]
  onTabChange: (tab: FormManagementTabId) => void
}

export function FormManagementNav({ activeTab, tabs, onTabChange }: FormManagementNavProps) {
  function focusTab(tab: FormManagementTabId, currentTarget: EventTarget & HTMLElement) {
    window.requestAnimationFrame(() => {
      currentTarget.querySelector<HTMLButtonElement>(`[data-management-tab="${tab}"]`)?.focus()
    })
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!activeTab) return
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < 0) return

    const keyMap: Partial<Record<string, number>> = {
      ArrowDown: Math.min(currentIndex + 1, tabs.length - 1),
      ArrowRight: Math.min(currentIndex + 1, tabs.length - 1),
      ArrowUp: Math.max(currentIndex - 1, 0),
      ArrowLeft: Math.max(currentIndex - 1, 0),
      Home: 0,
      End: tabs.length - 1,
    }
    const nextIndex = keyMap[event.key]
    if (typeof nextIndex !== 'number') return

    event.preventDefault()
    const nextTab = tabs[nextIndex]
    onTabChange(nextTab)
    focusTab(nextTab, event.currentTarget)
  }

  return (
    <aside className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-hidden border-b border-slate-200 bg-white lg:border-r lg:border-b-0">
      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <div className="text-sm font-semibold text-slate-950">管理工作區</div>
      </div>
      <ScrollArea
        className="min-h-0 w-full max-w-full flex-1"
        viewportClassName="max-w-full lg:p-3"
      >
        <div
          className="flex w-full min-w-0 max-w-full flex-wrap gap-2 p-3 lg:flex-col lg:flex-nowrap lg:gap-1 lg:p-0"
          role="tablist"
          aria-label="問卷管理功能"
          onKeyDown={handleKeyDown}
        >
          {tabs.map((tab) => {
            const Icon = tabIcons[tab]
            const active = activeTab === tab
            return (
              <Button
                key={tab}
                data-management-tab={tab}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`form-management-panel-${tab}`}
                id={`form-management-tab-${tab}`}
                tabIndex={active ? 0 : -1}
                variant="ghost"
                className={cn(
                  'h-10 justify-start gap-2 rounded-md px-3 text-sm lg:w-full',
                  active
                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100 hover:bg-indigo-50'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                )}
                onClick={() => onTabChange(tab)}
              >
                <Icon className="size-4" />
                {formManagementTabLabels[tab]}
              </Button>
            )
          })}
        </div>
      </ScrollArea>
    </aside>
  )
}
